import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { Icon } from './Icon';
import { PhoneActions } from './PhoneActions';
import { usePhoneActions } from './phoneActionsContext';

/**
 * ONE PHONE SCREEN — the phone artboards' `.screen`, as a real viewport.
 *
 * The top strip (☰, an optional back, an optional crumb, the title, an
 * optional trailing control) over a list that scrolls under it. Every phone
 * board in every lane is this shape, which is why it is here and not in a
 * lane: the technician's was the first and would otherwise have been the only
 * one, with five more lanes each writing their own top strip slightly
 * differently.
 *
 * THE MENU IS THE LANE'S, NOT THE KIT'S. ☰ has to open the doors THIS role
 * holds, with this role's live counts under them — a technician's Worklist and
 * a doctor's Today are not interchangeable and no generic list could carry
 * either. So the kit owns the button and the lane owns what it opens: pass
 * `menu` as a render prop and it is mounted, open, when ☰ is pressed.
 *
 * WHY A RENDER PROP AND NOT A NODE. A burger menu that is mounted-but-hidden
 * runs its lane's count queries on every phone screen whether or not anybody
 * opens it. Called only when it is opened, those reads happen when somebody
 * actually asks.
 *
 * THE PAGE'S VERBS, ON REQUEST. `actions` puts the primary and secondary the
 * page published to the shell into the strip (see PhoneActions) — so a screen
 * whose board draws the desk's "+ New flow" there does not restate it. Off by
 * default: plenty of boards draw this strip without them. Under a `crumb`
 * only the primary is drawn — the crumbed-strip rule the fallback shares.
 *
 * `ShellChrome.phone` is what puts this on screen: a page publishes one and
 * `MaieShell` swaps to it below the desk breakpoint (1023.98px), leaving the
 * routed page mounted because it is what publishes the layout.
 */
export function PhoneScreen({
  title,
  sub,
  crumb,
  onBack,
  trailing,
  actions,
  menu,
  search,
  className,
  children,
}: {
  title: ReactNode;
  /** A caption under the title ("Maadi"). */
  sub?: ReactNode;
  /** The muted "Worklist ▸" before the title. */
  crumb?: ReactNode;
  onBack?: () => void;
  trailing?: ReactNode;
  /** Draw the page's published primary/secondary in the strip (when no `trailing`). */
  actions?: boolean;
  /** This role's ☰, rendered only once it is opened. */
  menu?: (close: () => void) => ReactNode;
  /**
   * The screen's live search, drawn at the top of the list the way the phone
   * boards draw it ("Name or scan a barcode"). A screen with nothing to search
   * leaves it off.
   */
  search?: { placeholder: string; value: string; onChange: (next: string) => void };
  className?: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const verbs = usePhoneActions();
  return (
    <div className={`maie phone-screen${className ? ` ${className}` : ''}`}>
      <div className="topstrip">
        <button
          type="button"
          className="ts-btn"
          onClick={() => setOpen(true)}
          aria-label="Menu"
        >
          <Icon name="menu-2" size="lg" />
        </button>
        {onBack ? (
          <button type="button" className="ts-btn" onClick={onBack} aria-label="Back">
            <Icon name="chevron-left" size="lg" />
          </button>
        ) : null}
        <div className="grow" style={{ minWidth: 0 }}>
          {/* THE CRUMB GIVES WAY, NEVER THE SCREEN'S OWN NAME. On a 390px strip
              something has to ellipsise, and "… ▸ Can't" tells you where you
              are where "Worklist ▸ Ca…" does not. */}
          <CrumbTitle crumb={crumb} title={title} />
          {sub ? <p className="t-cap mut">{sub}</p> : null}
        </div>
        {/* Under a crumb the strip keeps only the primary, as the shell
            fallback does: the crumb needs the room a second chip takes. */}
        {trailing ?? (actions
          ? <PhoneActions primary={verbs.primary} secondary={crumb ? undefined : verbs.secondary} />
          : null)}
      </div>
      <div className="scroll">
        <div style={{ height: 12 }} />
        {search ? (
          <div className="srch">
            <Icon name="search" />
            <input
              className="grow"
              value={search.value}
              placeholder={search.placeholder}
              aria-label={search.placeholder}
              onChange={(e) => search.onChange(e.target.value)}
            />
          </div>
        ) : null}
        {children}
      </div>
      {open ? menu?.(() => setOpen(false)) : null}
    </div>
  );
}

/**
 * The strip's name line. The crumb gives way to the title — but a crumb squeezed
 * to a letter or two ("A" before "Expenses · Oct") is worse than none, so when
 * the room left over cannot hold all of it, it is not drawn at all. Measured
 * from the two natural widths, so hiding it cannot flip it back.
 */
function CrumbTitle({ crumb, title }: { crumb?: ReactNode; title: ReactNode }) {
  const box = useRef<HTMLParagraphElement>(null);
  const crumbEl = useRef<HTMLSpanElement>(null);
  const titleEl = useRef<HTMLSpanElement>(null);
  const [showCrumb, setShowCrumb] = useState(true);
  useLayoutEffect(() => {
    if (!crumb || !box.current || !titleEl.current) return undefined;
    const measure = () => {
      const room = (box.current?.clientWidth ?? 0) - (titleEl.current?.scrollWidth ?? 0);
      const want = crumbEl.current?.scrollWidth ?? 0;
      /* Show it whole or not at all: "Repo…" with its arrow clipped, running into
         the title, reads as a broken label — the back chevron already says where up is. */
      setShowCrumb(room >= want);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(box.current);
    return () => ro.disconnect();
  }, [crumb, title]);
  return (
    <p ref={box} className="ts-title" style={{ display: 'flex', minWidth: 0 }}>
      {crumb ? (
        <span
          ref={crumbEl}
          className="mut"
          style={{
            fontWeight: 400,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            /* Hidden by width, not display:none — a display:none box reports no
               natural width, and the measure above would flip it straight back. */
            ...(showCrumb ? null : { width: 0, flexShrink: 0, visibility: 'hidden' as const }),
          }}
        >
          {crumb}&nbsp;▸&nbsp;
        </span>
      ) : null}
      <span
        ref={titleEl}
        style={{
          flexShrink: crumb ? 0 : 1,
          minWidth: 0,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {title}
      </span>
    </p>
  );
}
